/**
 * MessageActions - Actions for chat messages
 *
 * Features:
 * - Regenerate button (assistant messages only)
 * - Edit button (user messages only)
 * - Delete button with confirmation
 * - Menu dropdown for mobile
 * - Copy is shown inside the message bubble (MessageContent)
 */

import { RefreshCw01, Edit01, Trash01, DotsVertical } from "@untitledui/icons";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface MessageActionsProps {
    content: string;
    role: "user" | "assistant";
    onRegenerate?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    isMobile?: boolean;
}

export const MessageActions = ({
    content: _content,
    role,
    onRegenerate,
    onEdit,
    onDelete,
    isMobile: isMobileProp,
}: MessageActionsProps) => {
    const { t } = useTranslation(['common']);
    const [showMenu, setShowMenu] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const actualIsMobile = isMobileProp !== undefined ? isMobileProp : isMobile;

    const handleDelete = () => {
        if (window.confirm(t('aiChat.actions.deleteConfirm', { defaultValue: "Are you sure you want to delete this message?" }))) {
            onDelete?.();
        }
    };

    if (actualIsMobile) {
        return (
            <div className="relative">
                <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-1.5 rounded-md hover:bg-secondary text-tertiary hover:text-primary transition-colors"
                    aria-label="More actions"
                >
                    <DotsVertical className="size-4" />
                </button>
                {showMenu && (
                    <div className="absolute right-0 top-8 z-10 bg-primary border border-secondary rounded-lg shadow-lg p-1 min-w-[120px]">
                        {role === "assistant" && onRegenerate && (
                            <button
                                onClick={() => {
                                    onRegenerate();
                                    setShowMenu(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-secondary rounded-md flex items-center gap-2"
                            >
                                <RefreshCw01 className="size-4" />
                                {t('aiChat.actions.regenerate', { defaultValue: "Regenerate" })}
                            </button>
                        )}
                        {role === "user" && onEdit && (
                            <button
                                onClick={() => {
                                    onEdit();
                                    setShowMenu(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-secondary rounded-md flex items-center gap-2"
                            >
                                <Edit01 className="size-4" />
                                {t('aiChat.actions.edit', { defaultValue: "Edit" })}
                            </button>
                        )}
                        {onDelete && (
                            <button
                                onClick={() => {
                                    handleDelete();
                                    setShowMenu(false);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-secondary rounded-md flex items-center gap-2 text-red-500"
                            >
                                <Trash01 className="size-4" />
                                {t('aiChat.actions.delete', { defaultValue: "Delete" })}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {role === "assistant" && onRegenerate && (
                <button
                    onClick={onRegenerate}
                    className="p-1.5 rounded-md hover:bg-secondary text-tertiary hover:text-primary transition-colors"
                    aria-label="Regenerate response"
                >
                    <RefreshCw01 className="size-4" />
                </button>
            )}
            {role === "user" && onEdit && (
                <button
                    onClick={onEdit}
                    className="p-1.5 rounded-md hover:bg-secondary text-tertiary hover:text-primary transition-colors"
                    aria-label="Edit message"
                >
                    <Edit01 className="size-4" />
                </button>
            )}
            {onDelete && (
                <button
                    onClick={handleDelete}
                    className="p-1.5 rounded-md hover:bg-secondary text-tertiary hover:text-primary transition-colors text-red-500"
                    aria-label="Delete message"
                >
                    <Trash01 className="size-4" />
                </button>
            )}
        </div>
    );
};

